import type { PaidMediaOverviewPlatform } from "src/services/PaidMediaOverviewAPI";
import {
  buildMockCampaignMetrics,
  buildMockDailyFacts,
  bucketMockFacts,
  mockPlatformTotals,
  mockPriorPeriod,
  mockSeededRatio,
  sumMockFacts,
  type MockDailyFact,
  type MockGranularity,
} from "./paidMediaOverviewMockData";

/**
 * Report-level mock data built from the same daily facts and campaign mix as the
 * overview, so a report tab and the overview agree on the same period.
 */

export type PlatformReportStatus = "active" | "learning" | "paused";

export interface PlatformReportTotals {
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  revenue: number;
  ctr: number | null;
  cpc: number | null;
  cpm: number | null;
  costPerPurchase: number | null;
  roas: number | null;
  conversionRate: number | null;
}

export interface PlatformReportRow {
  id: string;
  name: string;
  parentId: string | null;
  parentName: string | null;
  descriptor: string;
  status: PlatformReportStatus;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  revenue: number;
  ctr: number | null;
  cpc: number | null;
  costPerPurchase: number | null;
  roas: number | null;
}


export interface PlatformReportBundle {
  totals: PlatformReportTotals;
  priorTotals: PlatformReportTotals;
  series: MockDailyFact[];
  campaigns: PlatformReportRow[];
  groups: PlatformReportRow[];
  creatives: PlatformReportRow[];
}

const round2 = (value: number): number => Math.round(value * 100) / 100;

const ratio = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? round2(numerator / denominator) : null;

const percent = (part: number, whole: number): number | null =>
  whole > 0 ? round2((part / whole) * 100) : null;

const GROUP_LABELS: Record<PaidMediaOverviewPlatform, string[]> = {
  meta_ads: ["Broad Audience", "Lookalike 2%", "Retargeting 14D"],
  google_ads: ["Exact Match", "Phrase Match", "Broad Match"],
  tiktok_ads: ["Interest — Fashion & Beauty", "Lookalike Purchasers", "Broad Auto-Targeting"],
  snapchat_ads: ["Riyadh & Jeddah 18-34", "Lookalike Purchasers", "Interest — Shopping"],
};

const CREATIVE_LABELS: Record<PaidMediaOverviewPlatform, string[]> = {
  meta_ads: ["Carousel — Best Sellers", "Single Video — Unboxing", "Collection — New In"],
  google_ads: ["Responsive Search Ad A", "Responsive Search Ad B", "Product Listing Group"],
  tiktok_ads: ["Creator Try-On 15s", "Sound-On Hook 9s", "Catalogue Carousel"],
  snapchat_ads: ["Story Ad — Weekend Drop", "Single Image — Bestseller", "AR Try-On Lens"],
};

const GROUPS_PER_CAMPAIGN = 2;
const CREATIVES_PER_GROUP = 2;

const statusFor = (seed: string, roas: number | null, benchmark: number | null): PlatformReportStatus => {
  const draw = mockSeededRatio(`${seed}|status`);
  if (draw > 0.88) {
    return "paused";
  }
  if (roas !== null && benchmark !== null && roas < benchmark * 0.7) {
    return "learning";
  }
  return "active";
};

const toTotals = (facts: MockDailyFact[]): PlatformReportTotals => {
  const summed = sumMockFacts(facts);
  return {
    spend: summed.spend,
    impressions: summed.impressions,
    clicks: summed.clicks,
    purchases: summed.purchases,
    revenue: summed.revenue,
    ctr: percent(summed.clicks, summed.impressions),
    cpc: ratio(summed.spend, summed.clicks),
    cpm: ratio(summed.spend * 1000, summed.impressions),
    costPerPurchase: ratio(summed.spend, summed.purchases),
    roas: ratio(summed.revenue, summed.spend),
    conversionRate: percent(summed.purchases, summed.clicks),
  };
};

interface SplitInput {
  id: string;
  name: string;
  parentId: string | null;
  parentName: string | null;
  descriptor: string;
  spend: number;
  impressions: number;
  clicks: number;
  purchases: number;
  revenue: number;
}

const toRow = (input: SplitInput, benchmarkRoas: number | null): PlatformReportRow => {
  const roas = ratio(input.revenue, input.spend);
  return {
    ...input,
    status: statusFor(input.id, roas, benchmarkRoas),
    ctr: percent(input.clicks, input.impressions),
    cpc: ratio(input.spend, input.clicks),
    costPerPurchase: ratio(input.spend, input.purchases),
    roas,
  };
};

/**
 * Seeded split into `count` children: spend and revenue use separate weights so
 * children differ in efficiency, both normalised back to the parent's totals.
 */
const splitMetrics = (
  parent: Omit<SplitInput, "id" | "name" | "descriptor" | "parentId" | "parentName">,
  seed: string,
  count: number
): Array<Omit<SplitInput, "id" | "name" | "descriptor" | "parentId" | "parentName">> => {
  const spendWeights: number[] = [];
  const revenueWeights: number[] = [];

  for (let index = 0; index < count; index += 1) {
    spendWeights.push(0.6 + mockSeededRatio(`${seed}|spend|${index}`) * 0.8);
    revenueWeights.push(0.5 + mockSeededRatio(`${seed}|revenue|${index}`) * 1.1);
  }

  const spendTotal = spendWeights.reduce((sum, weight) => sum + weight, 0) || 1;
  const revenueTotal = revenueWeights.reduce((sum, weight) => sum + weight, 0) || 1;

  return spendWeights.map((weight, index) => {
    const spendShare = weight / spendTotal;
    const revenueShare = revenueWeights[index] / revenueTotal;
    return {
      spend: round2(parent.spend * spendShare),
      impressions: Math.round(parent.impressions * spendShare),
      clicks: Math.round(parent.clicks * spendShare),
      purchases: Math.round(parent.purchases * revenueShare),
      revenue: round2(parent.revenue * revenueShare),
    };
  });
};

export function buildPlatformReportMock(
  platform: PaidMediaOverviewPlatform,
  startDate: string,
  endDate: string
): PlatformReportBundle {
  const dailyFacts = buildMockDailyFacts(platform, startDate, endDate);
  const totals = toTotals(dailyFacts);

  const prior = mockPriorPeriod(startDate, endDate);
  const priorTotals = toTotals(buildMockDailyFacts(platform, prior.startDate, prior.endDate));

  const periodTotals = mockPlatformTotals(platform, startDate, endDate);
  const campaignMetrics = buildMockCampaignMetrics(platform, periodTotals);
  const benchmarkRoas = totals.roas;

  const campaigns = campaignMetrics.map(campaign =>
    toRow(
      {
        id: campaign.campaignId,
        name: campaign.campaignName,
        parentId: null,
        parentName: null,
        descriptor: campaign.objectiveOrNetwork,
        spend: campaign.spend,
        impressions: campaign.impressions,
        clicks: campaign.clicks,
        purchases: campaign.purchases,
        revenue: campaign.revenue,
      },
      benchmarkRoas
    )
  );

  const groupLabels = GROUP_LABELS[platform] ?? [];
  const creativeLabels = CREATIVE_LABELS[platform] ?? [];

  const groups: PlatformReportRow[] = [];
  const creatives: PlatformReportRow[] = [];

  campaigns.forEach(campaign => {
    const groupSplits = splitMetrics(campaign, `${campaign.id}|group`, GROUPS_PER_CAMPAIGN);

    groupSplits.forEach((metrics, groupIndex) => {
      const label = groupLabels[groupIndex % groupLabels.length] ?? `Group ${groupIndex + 1}`;
      const group = toRow(
        {
          id: `${campaign.id}-g${groupIndex + 1}`,
          name: label,
          parentId: campaign.id,
          parentName: campaign.name,
          descriptor: campaign.descriptor,
          ...metrics,
        },
        benchmarkRoas
      );
      groups.push(group);

      splitMetrics(group, `${group.id}|creative`, CREATIVES_PER_GROUP).forEach(
        (creativeMetrics, creativeIndex) => {
          const creativeLabel =
            creativeLabels[(groupIndex + creativeIndex) % creativeLabels.length] ??
            `Creative ${creativeIndex + 1}`;
          creatives.push(
            toRow(
              {
                id: `${group.id}-c${creativeIndex + 1}`,
                name: creativeLabel,
                parentId: group.id,
                parentName: group.name,
                descriptor: campaign.name,
                ...creativeMetrics,
              },
              benchmarkRoas
            )
          );
        }
      );
    });
  });

  return { totals, priorTotals, series: dailyFacts, campaigns, groups, creatives };
}

export function bucketPlatformReportSeries(
  series: MockDailyFact[],
  granularity: MockGranularity
): MockDailyFact[] {
  return bucketMockFacts(series, granularity);
}
